export const publicLabsCatalog = {
  browseMetaTitle: "Blood and Specialty Lab Catalog | 2,400+ Tests",
  browseH1: "Blood and specialty labs in one catalog",
  browseMetaDescription:
    "Browse 2,400+ blood panels and specialty tests from 20+ lab companies. Order, track results, and build plans in Fullscript. Practitioner pricing after signup.",
  browseDescription: "Browse functional lab tests and panels.",
  hubContent: {
    intro:
      "Fullscript Labs is a catalog of blood panels and specialty tests for practitioners. Browse 2,400+ tests from 20+ lab companies, then manage orders and results alongside patient care. Practitioner pricing is available after you <registerLink>register for labs</registerLink>.",
    bloodLabsHeading: "Blood labs",
    bloodLabsBody:
      "Blood labs include foundational panels and markers for routine screening, preventive care, and baseline testing. Collection depends on the test and lab company. Check each test page for details.",
    catalogOverviewLink: "Learn more about blood labs and specialty tests",
    specialtyTestsHeading: "Specialty tests",
    specialtyTestsBody:
      "Specialty tests support questions related to gut health, hormones, environmental exposures, immunology, genetics, and more. Many use at-home collection kits, and some can be stocked in your office. Check each test page for biomarkers, sample type, and collection details.",
    howToFindHeading: "How to find and order tests",
    howToFindStep1: "<registerLink>Register for labs</registerLink> in your Fullscript account.",
    howToFindStep2:
      "Search or filter the catalog—or open a test page for full details. See the <overviewLink>lab catalog overview</overviewLink>.",
    howToFindStep3: "Add tests to a patient plan and send.",
    howToFindStep4:
      "Review results in Fullscript, then share them with your patient when you're ready. See <resultsLink>Managing lab results and sharing with patients</resultsLink>.",
    browseByCompanyHeading: "Browse by lab company",
    browseAllLabCompanies: "View all lab companies",
    faq: {
      title: "Frequently asked questions",
      q1: {
        question: "What's the difference between blood labs and specialty tests?",
        answer:
          "Blood labs include foundational panels and markers for routine screening, preventive care, and baseline testing. Specialty tests support more targeted questions related to gut health, hormones, environmental exposures, immunology, genetics, and more. Many specialty tests use at-home collection kits. See the labs catalog overview for collection details.",
        answerRich:
          "Blood labs include foundational panels and markers for routine screening, preventive care, and baseline testing. Specialty tests support more targeted questions related to gut health, hormones, environmental exposures, immunology, genetics, and more. Many specialty tests use at-home collection kits. See the <overviewLink>labs catalog overview</overviewLink> for collection details.",
      },
      q2: {
        question: "How do I get access to order labs?",
        answer:
          "Providers register for labs in Fullscript. Ordering access depends on each lab company's requirements. If you need a signing clinician, Fullscript supports clinic signing practitioners and an authorization network option. Steps and current details are in Registering for labs.",
        answerRich:
          "Providers register for labs in Fullscript. Ordering access depends on each lab company's requirements. If you need a signing clinician, Fullscript supports clinic signing practitioners and an authorization network option. Steps and current details are in <registerLink>Registering for labs</registerLink>.",
      },
      q3: {
        question: "How are Rupa Health and Fullscript Labs coming together?",
        answer:
          "Rupa Health and Fullscript are coming together to support labs and whole-person care in one platform. See Moving your Rupa account to Fullscript for current details.",
        answerRich:
          "Rupa Health and Fullscript are coming together to support labs and whole-person care in one platform. See <rupaLink>Moving your Rupa account to Fullscript</rupaLink> for current details.",
      },
      q4: {
        question: "Where do results show up?",
        answer:
          "Results for labs ordered through Fullscript appear in your Fullscript account. You control when patients are notified that results are ready to review. See Managing lab results and sharing with patients.",
        answerRich:
          "Results for labs ordered through Fullscript appear in your Fullscript account. You control when patients are notified that results are ready to review. See <resultsLink>Managing lab results and sharing with patients</resultsLink>.",
      },
      q5: {
        question: "Which lab companies can I order from?",
        answer:
          "The Fullscript Labs catalog includes blood and specialty partners. Browse the full, current list on Lab companies, or filter this page by company.",
        answerRich:
          "The Fullscript Labs catalog includes blood and specialty partners. Browse the full, current list on <companiesLink>Lab companies</companiesLink>, or filter this page by company.",
      },
    },
  },
  companiesTitle: "Lab companies",
  companiesSubtitle:
    "Compare {{count}} lab company in the Fullscript catalog. Sign up as a practitioner to see pricing and order tests.",
  companiesSubtitle_plural:
    "Compare {{count}} lab companies in the Fullscript catalog. Sign up as a practitioner to see pricing and order tests.",
  companyPageTitle: "{{name}} lab tests",
  companyPageDescription:
    "Browse {{count}} lab test from {{name}} in the Fullscript Labs catalog. Sign up as a practitioner to see pricing and order through Fullscript.",
  companyPageDescription_plural:
    "Browse {{count}} lab tests from {{name}} in the Fullscript Labs catalog. Sign up as a practitioner to see pricing and order through Fullscript.",
  companyTestsCount: "{{count}} test",
  companyTestsCount_plural: "{{count}} tests",
  categoryTitle: "{{category}} lab tests",
  categoryDescription:
    "Browse {{count}} lab test in {{category}} from Fullscript's catalog. Sign up as a practitioner to see pricing and order.",
  categoryDescription_plural:
    "Browse {{count}} lab tests in {{category}} from Fullscript's catalog. Sign up as a practitioner to see pricing and order.",
  logIn: "Sign in",
  signUp: "Create account",
  goToLabsCatalog: "Go to labs catalog",
  chooseAccount: "Choose account",
  viewInCatalog: "View in catalog",
  pricingInYourCatalog: "Pricing available in your catalog",
  homeAriaLabel: "Fullscript",
  mobileNavMenuAriaLabel: "Open navigation menu",
  searchLabel: "Search",
  searchPlaceholder: "Search",
  companyFilter: "Company",
  companyFilterAll: "All",
  healthCategoryFilter: "Health category",
  healthCategoryFilterAll: "All",
  sampleTypeFilter: "Sample type",
  sampleTypeFilterAll: "All",
  sortLabel: "Sort",
  sortPopularity: "Most popular",
  sortBestMatch: "Best match",
  sortAlphabeticalAsc: "Alphabetical (A - Z)",
  sortAlphabeticalDesc: "Alphabetical (Z - A)",
  tableHeaderLabTest: "Lab test",
  tableHeaderSampleType: "Sample type",
  tableHeaderPrice: "Price",
  signUpForPractitionerPricing: "Sign up for practitioner pricing",
  emptyTitle: "No lab tests found",
  emptyDescription: "Try a different search or clear the filters.",
  emptyClearFilters: "Clear all filters",
  paginationPrevious: "Previous",
  paginationNext: "Next",
  paginationPage: "Page {{page}}",
  breadcrumbAriaLabel: "Breadcrumb",
  breadcrumbCatalog: "Labs catalog",
  breadcrumbCompanies: "Lab companies",
  detailDescription: "{{name}} from {{companyName}} in the Fullscript Labs catalog.",
  patientTerm: "Patient",
  addOnsAvailable: "Add-ons available",
  detailSidebar: {
    pricingBadge: "Provider pricing unlocks when you sign up",
    headline: "Go from test to plan, in one place",
    benefit1: "2,400+ blood labs and specialty tests from partners you trust, one login",
    benefit2: "Turn results into personalized plans with <bold>Fullscript Assist</bold>",
    benefit3: "Competitive cash-pay pricing and <bold>nationwide phlebotomy</bold>",
    createFreeAccount: "Create free account",
    seeInLabsCatalog: "See in labs catalog",
    bookDemo: "Book a demo",
    trustedBy: "Trusted by 125,000+ practitioners",
    diagnosticPartnersCount: "20+",
    diagnosticPartnersLabel: "lab companies",
    ehrIntegrationsCount: "40+",
    ehrIntegrationsLabel: "EHR integrations",
    testimonialQuote:
      "Having labs on Fullscript has been a real time saver for my practice. I love the convenience and ability to order affordable labs quickly for my patients. It will be a staple in my workflow going forward.",
    testimonialName: "Joseph Thomas, MD",
    testimonialSubtitle: "Verified Fullscript practitioner",
    testimonialAvatarAlt: "Joseph Thomas",
    partnerLogoAlts: {
      quest: "Quest Diagnostics",
      accessMedical: "Access Medical Labs",
      threeByFourGenetics: "3x4 Genetics",
      practiceBetter: "Practice Better",
      cerbo: "Cerbo",
      optimantra: "OptiMantra",
    },
  },
};
